/**
 * Observability integration points.
 *
 * No vendor is wired up — that is a deployment decision. These are the seams a
 * Sentry/Datadog init would plug into, and they enforce the redaction rules so that
 * choice cannot accidentally start shipping tokens or customer PII.
 */

import { env } from '@/app/config/env'
import { isApiError } from '@/services/api/errors'

export interface ObservabilityContext {
  userId?: string
  tenantId?: string
  role?: string
}

let context: ObservabilityContext = {}

/** Called after login so every subsequent report carries who and which tenant. */
export function setObservabilityContext(next: ObservabilityContext): void {
  context = { ...context, ...next }
}

export function clearObservabilityContext(): void {
  context = {}
}

/**
 * Keys that must never leave the browser in a log or error report.
 * Tokens and customer PII are both on this list deliberately.
 */
const FORBIDDEN_KEYS = new Set([
  'access_token',
  'refresh_token',
  'pre_auth_token',
  'authorization',
  'password',
  'code',
  'otp',
  'phone',
  'customer_phone',
  'customer_email',
  'customer_name',
  'email',
])

export function scrub(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(payload)) {
    if (FORBIDDEN_KEYS.has(key.toLowerCase())) continue
    out[key] = value
  }
  return out
}

export function reportError(error: unknown, extra: Record<string, unknown> = {}): void {
  const payload = {
    ...scrub(extra),
    ...context,
    ...(isApiError(error)
      ? { apiKind: error.kind, apiCode: error.code, status: error.status, correlationId: error.correlationId }
      : {}),
  }

  // Replace with the real sink (Sentry.captureException, etc.) at deploy time.
  if (env.isDevelopment) {
    console.error('[observability]', error, payload)
  }
}

/** API failures worth watching as a rate, separate from thrown exceptions. */
export function reportApiFailure(path: string, error: unknown): void {
  if (isApiError(error) && (error.kind === 'cancelled' || error.kind === 'unauthenticated')) return
  reportError(error, { path })
}
